import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { AppState } from '@capacitor/app';
import { AlertController, Platform, ToastController } from '@ionic/angular';
import { Subject, Subscription } from 'rxjs';
import { LOCAL_NOTIFICATION_ACTION_TYPE } from '../enums/notification/notification.enum';
import { Notification, NotificationData, NotificationInput } from '../models/notification/notification-types';
import { ToastConfig } from '../models/notification/toast-config-interface';
import { AlertNotificationService } from './alert-notification.service';
import { HttpService } from './http.service';
import { LocalNotificationService } from './local-notification.service';
import { PushNotificationService } from './push-notification.service';
import { ToastNotificationService } from './toast-notification.service';
import { GlobalConstants } from '../constant/constant';
import { DateTimeHelper } from '../utils/date-time-helper';
import { AuthSessionService } from './auth/auth-session.service';
import { AppStateService } from './app-state.service';
import { PushNotificationSchema } from '@capacitor/push-notifications';
import { LocalNotificationSchema } from '@capacitor/local-notifications';

export enum TOAST_CLASS {
  SUCCESS = 'form-submit-toast-success',
  WARNING = 'form-submit-toast-warning',
  DANGER = 'form-submit-toast-danger',
}

export type EmbeddedSoundFile = {
  audioKey: string;
  audioPath: string;
};

@Injectable({
  providedIn: 'root',
})
export class NotificationsService implements OnDestroy {
  private appState: AppState;

  private push: PushNotificationService;
  private local: LocalNotificationService;
  private toast: ToastNotificationService;
  private alert: AlertNotificationService;

  private notifications: Notification[] = [];
  private emitReceivedNotification = new Subject<Notification>();
  notificationReceived = this.emitReceivedNotification.asObservable();
  private emitPerformedNotification = new Subject<Notification>();
  notificationPerformed = this.emitPerformedNotification.asObservable();
  private pushNotificationsSubscription: Subscription;
  private localNotificationsSubscription: Subscription;
  private sessionSubscription: Subscription;

  readonly LOCAL_NOTIFICATION_TYPES = [
    {
      id: LOCAL_NOTIFICATION_ACTION_TYPE.ACTIVITY_LOG.toString(),
      actions: [
        {
          id: 'enter',
          title: 'Enter Activity Log',
          url: 'app/activityLog',
        },
      ],
    },
  ];

  constructor(
    private http: HttpService,
    private sessions: AuthSessionService,
    private appStateService: AppStateService,
    private platform: Platform,
    private toastController: ToastController,
    private alertController: AlertController,
    private router: Router,
  ) {
    this.push = new PushNotificationService();
    this.local = new LocalNotificationService(this.platform);
    this.toast = new ToastNotificationService(this.toastController);
    this.alert = new AlertNotificationService(this.alertController);

    this.sessionSubscription = this.sessions.identity$.subscribe(identity => {
      if (identity) { void this.updateUserFirebaseToken(); }
    });

    this.appStateService.getAppState().subscribe(async appState => (this.appState = appState));
  }

  get isMobile() {
    return this.platform.is('hybrid');
  }

  ngOnDestroy() {
    this.sessionSubscription.unsubscribe();
    this.pushNotificationsSubscription?.unsubscribe();
    this.localNotificationsSubscription?.unsubscribe();
  }

  registerNotifications() {
    this.push.registerPush();
    this.addListeners();
  }

  private addListeners() {
    this.push.listenForEvents();
    this.local.listenForEvents(this.LOCAL_NOTIFICATION_TYPES);

    this.pushNotificationsSubscription = this.push.pushNotificationChange.subscribe(async event => {
      if (this.appState.isActive) {
        event.data.isRead = true;
      }

      if (event.type === 'received') {
        this.emitReceivedNotification.next({ data: event.data, type: 'push' });
        this.notifications.push({ data: event.data, type: 'push' });
      }

      if (event.type === 'performed') {
        this.emitPerformedNotification.next({ data: event.data, type: 'push' });
      }
    });

    this.localNotificationsSubscription = this.local.localNotificationChange.subscribe(async event => {
      if (this.appState.isActive) {
        event.data.isRead = true;
      }

      if (event.type === 'received') {
        this.emitReceivedNotification.next({ data: event.data, type: 'local' });
        this.notifications.push({ data: event.data, type: 'local' });
      }

      if (event.type === 'performed') {
        this.emitPerformedNotification.next({ data: event.data, type: 'local' });
      }

      if (event.type === 'received') {
        const toastConfig: ToastConfig = { message: event.data.content.body, header: event.data.content.title };
        if (event.data.content.actionTypeId && event.data.content.actionTypeId in LOCAL_NOTIFICATION_ACTION_TYPE) {
          const action = this.LOCAL_NOTIFICATION_TYPES.find(type => type.id === event.data.content.actionTypeId);
          if (action) {
            toastConfig.buttons = [
              {
                text: action.actions[0].title,
                ...(action.actions[0].url && { handler: () => this.router.navigate([action.actions[0].url], { replaceUrl: true }) }),
              },
            ];
          }
        }
        this.showToast(toastConfig);
      }
    });
  }

  displayInAppNotification(notification: PushNotificationSchema) {
    const uiMode = notification.data.UIMode;

    if (uiMode === 'toster') {
      this.showToast({ message: notification.data.body, header: notification.data.title });
    } else {
      this.showAlert({ header: notification.data.title, message: notification.data.body, buttons: [{text: 'Close', role: 'cancel'}] });
    }
  }

  async showToast(notification: NotificationInput<'toast'>) {
    await this.toast.show(notification);
  }

  async showAlert(notification: NotificationInput<'alert'>) {
    const alert = await this.alert.show(notification);
    return alert;
  }

  async scheduleLocalNotification(notification: NotificationInput<'local'>) {
    await this.local.scheduleLocalNotification(notification);
  }

  async cancelLocalNotification(id: number) {
    await this.local.cancelScheduledNotificationById(id);
  }

  async markNotificationAsRead(notification: NotificationData) {
    notification.isRead = true;
  }

  async markAllNotificationsAsRead() {
    this.notifications.forEach(notification => {
      notification.data.isRead = true;
    });
  }

  async updateUserFirebaseToken() {
    const token = localStorage.getItem(GlobalConstants.FIREBASE_TOKEN);

    if (token) {
      const request = { Token: token, Timestamp: DateTimeHelper.getUtcNowString() };
      try {
        await this.http.post(GlobalConstants.PUSH_REGISTRATION_URI, request).toPromise();
      } catch (err) {
        console.log(`Failed to update user token: ${err}`);
      }
    }
  }

  removeDeliveredPushNotification(notification: PushNotificationSchema) {
    this.push.removeDeliveredNotifications({ notifications: [notification] });
  }

  removeDeliveredLocalNotification(notification: LocalNotificationSchema) {
    this.local.removeDeliveredNotifications({ notifications: [notification] });
  };
}
